"use client";

import { useState, useMemo, useEffect, useRef } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import Chevron from "~/assets/images/home/chevron-icon.svg";
import Collapsible from "~/common/components/Collapsible";
import Image from "~/common/components/CustomImage";
import useMediaQuery from "~/common/hooks/useMediaQuery";

import styles from "./CollapsibleHeroCollection.module.scss";

const DEFAULT_IMAGE_TRANSITION_DURATION = 500;

// next/image importları bazen obje ( { src: string, width, height, ... } ) şeklinde gelir. Bazı item'lar doğrudan string path olabilir.
function resolveImageSrc(img) {
  if (!img) return undefined;
  if (typeof img === "string") return img;
  if (typeof img === "object") {
    // next/image static import
    if (img.src) return img.src;
  }
  return undefined;
}

export default function CollapsibleHeroCollection({
  items = [],
  t,
  className,
  imageTransitionDuration = DEFAULT_IMAGE_TRANSITION_DURATION,
  imageContainerStyle = {},
}) {
  const first = useMemo(() => items[0], [items]);
  //
  const [activeId, setActiveId] = useState(first?.id);

  const activeItem = useMemo(() => items.find((i) => i.id === activeId), [items, activeId]);
  const isMdUp = useMediaQuery("md");
  const [recalcToken, setRecalcToken] = useState(0);

  const preloadedSetRef = useRef(new Set());
  const preloadQueueRef = useRef([]);
  const idleCallbackIdRef = useRef(null);

  const resolveSrc = (img) => resolveImageSrc(img);
  const resolveImageAlt = (item) => {
    const normalizeAlt = (value) =>
      String(value || "")
        .replace(/\s+/g, " ")
        .trim();
    if (t && item?.imageAltKey) return normalizeAlt(t(item.imageAltKey));
    if (item?.imageAlt) return normalizeAlt(item.imageAlt);
    if (t && item?.titleKey) return normalizeAlt(t(item.titleKey));
    if (item?.titleKey) return normalizeAlt(item.titleKey);
    return "";
  };

  const scheduleIdle = (fn) => {
    if (typeof window === "undefined") return;
    if ("requestIdleCallback" in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      setTimeout(fn, 120);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setRecalcToken((t) => t + 1);
    }, 200);
  }, [isMdUp]);

  useEffect(() => {
    if (typeof window === "undefined" || !items?.length) return;

    const all = items
      .map((it) => resolveSrc(it.image))
      .filter(Boolean)
      .filter((src, idx, arr) => arr.indexOf(src) === idx);

    const activeSrc = resolveSrc(activeItem?.image);
    const queue = all.filter((s) => s !== activeSrc);
    preloadQueueRef.current = queue;

    const run = () => {
      if (!preloadQueueRef.current.length) return;
      const next = preloadQueueRef.current.shift();
      if (!next || preloadedSetRef.current.has(next)) {
        scheduleIdle(run);
        return;
      }
      try {
        const img = new window.Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = next;
        img.onload = img.onerror = () => {
          preloadedSetRef.current.add(next);
          scheduleIdle(run);
        };
      } catch {
        scheduleIdle(run);
      }
    };

    scheduleIdle(run);

    return () => {
      if (typeof window !== "undefined" && idleCallbackIdRef.current && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
    };
  }, [items, activeItem]);

  const handleOpen = (newItem) => {
    if (!newItem) return;
    if (newItem.id === activeId) return;
    const newSrc = resolveSrc(newItem.image);
    if (!newSrc) return;
    if (typeof window !== "undefined" && !preloadedSetRef.current.has(newSrc)) {
      try {
        const img = new window.Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = newSrc;
        img.onload = () => preloadedSetRef.current.add(newSrc);
      } catch {}
    }
    setActiveId(newItem.id);
  };

  return (
    <div className={clsx(styles.container, "CollapsibleHeroCollection", className)}>
      <ol className={clsx(styles.dropdownContainer, "CollapsibleHeroCollection--dropdown-container")} style={{ listStyle: "none" }}>
        {items.map((item, index) => (
          <li key={item.id}>
          <Collapsible
            showCollapseIcon={false}
            recalcToken={recalcToken}
            title={
              <div className={clsx(styles.dropdownItemHeader, "CollapsibleHeroCollection--item-header")}>
                <div
                  className={clsx(
                    styles.iconWrapper,
                    styles[item.iconBgClass],
                    "CollapsibleHeroCollection--icon-wrapper",
                    `CollapsibleHeroCollection--icon-${item.iconBgClass}`
                  )}
                >
                  <Chevron />
                </div>
                <h3 className={clsx(styles.itemTitle, "CollapsibleHeroCollection--item-title")}>
                  {t ? t(item.titleKey) : item.titleKey}
                </h3>
              </div>
            }
            collapseGroupKey="collapsible-hero-collection"
            defaultOpen={index === 0}
            onOpen={() => handleOpen(item)}
            showTopBorder={false}
            className={clsx(
              styles.collapsibleItem,
              styles.noBorderCollapsible,
              "CollapsibleHeroCollection--collapsible-item"
            )}
          >
            <p className={clsx(styles.itemDescription, "CollapsibleHeroCollection--item-description")}>
              {t ? t(item.descriptionKey) : item.descriptionKey}
            </p>
            {!isMdUp && (
              <div
                className={clsx(
                  styles.imageContainer,
                  "CollapsibleHeroCollection--image-container",
                  "CollapsibleHeroCollection--image-container-mobile"
                )}
              >
                <Image
                  src={item.image}
                  alt={resolveImageAlt(item)}
                  width={530}
                  height={570}
                  priority
                  {...item.imageProps}
                />
              </div>
            )}
          </Collapsible>
          </li>
        ))}
      </ol>

      {isMdUp && (
        <div
          className={clsx(
            styles.imageContainer,
            "CollapsibleHeroCollection--image-container",
            "CollapsibleHeroCollection--image-container-desktop"
          )}
        >
          <div
            className={clsx(styles.imageFadeRoot, "CollapsibleHeroCollection--fade-root")}
            style={imageContainerStyle}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {activeItem && (
                <motion.div
                  key={activeItem.id}
                  className={clsx(styles.imageLayer, styles.imageLayerActive)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: imageTransitionDuration / 1000, ease: "easeInOut" }}
                >
                  <Image
                    src={activeItem.image}
                    alt={resolveImageAlt(activeItem)}
                    width={530}
                    height={570}
                    priority
                    {...activeItem.imageProps}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
